import React, { useState } from 'react';
import { Box } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { useMutation, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { ErrorNotification } from '@care/enterprise-components';
import StepCard from '@/components/shared/StepCard';
import StepCTA from '@/components/shared/StepCTA';
import TermsOfUse from '@/components/shared/TermsOfUse';
import { useEventLogger } from '@/utilities/analyticsHelper';
import {
  ENTERPRISE_ROUTES,
  EventNames,
  LOCAL_STORAGE_STATE_KEY,
  memberEnrollment,
  TEXTS,
} from '@/constants';
import VerifyInfo from '@/components/pages/MemberEnrollment/OneClick/components/VerifyInfo';
import { DirectEnrollment } from '@/types/directEnrollment';
import { getOneClickSchemaValidation } from '@/components/pages/MemberEnrollment/OneClick/components/schemaValidation';
import useDirectEnrollmentData from '@/components/hooks/useDirectEnrollmentData';
import {
  enrollEnterpriseEmployee,
  enrollEnterpriseEmployee_enrollSeekerForEnterpriseClient_EnterpriseEnrollmentProcessFailure as EnrollmentErrors,
  enrollEnterpriseEmployeeVariables,
} from '@/__generated__/enrollEnterpriseEmployee';
import {
  ENROLL_ENTERPRISE_EMPLOYEE,
  GET_ENTERPRISE_EMPLOYEE_VERIFICATION_RECORD,
} from '@/components/request/GQL';
import { SafeLocalStorage } from '@/utilities/localStorageHelper';
import AuthService from '@/lib/AuthService';
import {
  EnterpriseEmployeeInformation,
  EnterpriseEnrollmentErrorStatus,
} from '@/__generated__/globalTypes';
import OverlaySpinner from '@/components/shared/OverlaySpinner';
import {
  EnterpriseEnrollmentEmployeeVerification,
  EnterpriseEnrollmentEmployeeVerificationVariables,
} from '@/__generated__/EnterpriseEnrollmentEmployeeVerification';
import { logErrorViewed } from '@/components/pages/MemberEnrollment/OneClick/helpers/logErrorViewed';
import Landing from '@/components/pages/MemberEnrollment/OneClick/components/Landing';
import { passwordGenerate } from '@/components/pages/MemberEnrollment/OneClick/helpers/passwordGenerate';
import useEnterKey from '@/components/hooks/useEnterKey';

const {
  ERRORS: { REQUEST_FAILED },
  ONE_CLICK_ENROLLMENT: {
    STREET_ADDRESS,
    ZIP_CODE,
    ELIGIBILITY_FAILURE,
    VERIFY_INFO_STEP_NAME,
    WELCOME_LANDING_PAGE_STEP_NAME,
    ONE_CLICK_ENROLL_EVENT_NAME,
    ONE_CLICK_ENROLL_V2_EVENT_NAME,
  },
} = TEXTS;

const { WELCOME } = ENTERPRISE_ROUTES;

interface PageContentProps {
  groupName: string;
  employeeUUID: string;
  isOneClickV2: boolean;
}

const PageContent: React.FC<PageContentProps> = ({ groupName, employeeUUID, isOneClickV2 }) => {
  const router = useRouter();
  const authService = AuthService();
  const { update, fetch } = useDirectEnrollmentData();
  const [showVerifyInfo, setShowVerifyInfo] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<{
    text?: string;
    subText?: string | React.ReactNode;
  } | null>(null);
  const ctaType = 'create';
  const createAccount = 'Create account';
  const isOverlayOpen = isLoading || isSubmitting;
  const stepName = showVerifyInfo ? VERIFY_INFO_STEP_NAME : WELCOME_LANDING_PAGE_STEP_NAME;

  const logger = useEventLogger({
    user_flow: 'One-click enroll',
  });

  const handleEnrollEmployeeErrors = (errors: EnrollmentErrors) => {
    if (errors.errorStatus === EnterpriseEnrollmentErrorStatus.MEMBER_EXISTS) {
      router.push(`/${groupName}${ENTERPRISE_ROUTES.CONNECT_ACCOUNT}`);
      return;
    }
    setSubmitting(false);
    const errorMessage = errors.errors[0].errorMessages[0].split('<')[0];
    logErrorViewed(logger, {
      message: errorMessage,
      stepName,
    });
    if (
      errors.errorStatus === EnterpriseEnrollmentErrorStatus.ENT_INELIGIBLE_EMAIL ||
      errorMessage.includes('already taken')
    ) {
      setError({ text: ELIGIBILITY_FAILURE });
      return;
    }
    setError({ text: REQUEST_FAILED });
  };

  const [enrollEmployee] = useMutation<enrollEnterpriseEmployee, enrollEnterpriseEmployeeVariables>(
    ENROLL_ENTERPRISE_EMPLOYEE,
    {
      context: { skipAuth: true },
      onCompleted: async (res) => {
        const { __typename } = res.enrollSeekerForEnterpriseClient;
        if (__typename === 'EnterpriseEmployeeEnrollmentSuccess') {
          const { memberId } = res.enrollSeekerForEnterpriseClient;
          logger({
            name: EventNames.MEMBER_ENROLLED,
            data: {
              care_company_name: groupName,
              member_id: memberId,
            },
          });
          const appStateStringified =
            SafeLocalStorage.getInstance().getItem(LOCAL_STORAGE_STATE_KEY) || '{}';
          const appState = JSON.parse(appStateStringified);
          SafeLocalStorage.getInstance().setItem(
            LOCAL_STORAGE_STATE_KEY,
            JSON.stringify({
              ...appState,
              flow: { memberId },
            })
          );
          const nextPage = `${router.basePath}${memberEnrollment}${ENTERPRISE_ROUTES.SET_PASSWORD}`;
          await authService.redirectLogin(nextPage, res.enrollSeekerForEnterpriseClient.authToken);
        }
        if (__typename === 'EnterpriseEnrollmentProcessFailure') {
          handleEnrollEmployeeErrors(res.enrollSeekerForEnterpriseClient);
        }
      },
      onError: (err) => {
        logErrorViewed(logger, {
          message: err.message,
          stepName,
        });
        setSubmitting(false);
        setError({ text: REQUEST_FAILED });
      },
    }
  );
  useQuery<
    EnterpriseEnrollmentEmployeeVerification,
    EnterpriseEnrollmentEmployeeVerificationVariables
  >(GET_ENTERPRISE_EMPLOYEE_VERIFICATION_RECORD, {
    context: { skipAuth: true },
    variables: {
      input: {
        employeeVerificationId: employeeUUID,
        groupShortName: groupName,
      },
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (
        data?.enterpriseEnrollmentEmployeeVerification?.__typename ===
        'EnterpriseEnrollmentEmployeeVerificationSuccess'
      ) {
        const employeeVerificationData =
          data.enterpriseEnrollmentEmployeeVerification?.employeeVerification;

        const hasFirstOrLastName =
          employeeVerificationData?.firstName || employeeVerificationData?.lastName;
        if (!hasFirstOrLastName && !employeeVerificationData?.careGroupName) {
          evaluateOneClickLogger();
          logErrorViewed(logger, {
            message:
              'Employee verification response does not have firstName & lastName & careGroupName',
            stepName,
          });
          router.push(`${memberEnrollment}${WELCOME}`);
          return;
        }

        update({
          firstName: employeeVerificationData.firstName || '',
          lastName: employeeVerificationData.lastName || '',
          email: employeeVerificationData.emailAddress!,
          employeeId: employeeVerificationData.employeeId!,
          address: STREET_ADDRESS,
          zipCode: ZIP_CODE,
          groupShortName: groupName,
          dateOfBirth: null,
          groupDisplayName: employeeVerificationData?.careGroupName || undefined,
        });

        if (!employeeVerificationData?.firstName || !employeeVerificationData?.lastName) {
          logV1OneClickEnrollToAmplitude();
          setShowVerifyInfo(true);
        } else {
          evaluateOneClickLogger();
          setShowLandingPage(true);
        }
        setIsLoading(false);
      } else {
        evaluateOneClickLogger();
        const errorMessage = data.enterpriseEnrollmentEmployeeVerification?.rawErrorMessage;
        logErrorViewed(logger, {
          message: errorMessage,
          stepName,
        });
        router.push(`${memberEnrollment}${WELCOME}`);
      }
    },
    onError: (err) => {
      evaluateOneClickLogger();
      logErrorViewed(logger, {
        message: err.message,
        stepName,
      });
      router.push(`${memberEnrollment}${WELCOME}`);
      return <></>;
    },
  });

  const handleCreateAccount = (val: DirectEnrollment) => {
    if ((showVerifyInfo && isOneClickV2) || !isOneClickV2) {
      logger({
        name: EventNames.CTA_INTERACTED,
        data: {
          cta_text: createAccount,
          step_name: stepName,
        },
      });
    }

    // we only want to update these values if the user entered fname/lname
    // in the form, otherwise it will set the values to null
    if (showVerifyInfo) {
      update({
        firstName: val.firstName,
        lastName: val.lastName,
      });
    }
    setSubmitting(true);
    const { address, firstName, lastName, zipCode, email, employeeId } = fetch();
    const employeeInformation: EnterpriseEmployeeInformation = {
      address,
      firstName,
      lastName,
      email,
      zip: zipCode,
      employeeId,
      password: passwordGenerate(),
    };
    enrollEmployee({
      variables: {
        input: {
          group: groupName,
          employeeInformation,
        },
      },
    });
  };

  const evaluateOneClickLogger = () => {
    if (isOneClickV2) {
      logV2OneClickEnrollToAmplitude();
    } else {
      logV1OneClickEnrollToAmplitude();
    }
  };

  const logV1OneClickEnrollToAmplitude = () => {
    logger({
      name: ONE_CLICK_ENROLL_EVENT_NAME,
    });
  };

  const logV2OneClickEnrollToAmplitude = () => {
    logger({
      name: ONE_CLICK_ENROLL_V2_EVENT_NAME,
    });
  };

  const formik = useFormik<DirectEnrollment>({
    initialValues: {
      ...fetch(),
    },
    validationSchema: getOneClickSchemaValidation(),
    onSubmit: handleCreateAccount,
    validateOnMount: false,
    enableReinitialize: true,
  });

  // With the boolean check for showLandingPage, it'll infinite loop
  // because React will re-render continuously.
  if (showLandingPage && isOneClickV2) {
    handleCreateAccount(fetch());
    setShowLandingPage(false);
  }

  useEnterKey(!isOverlayOpen, formik.submitForm);

  return (
    <>
      <OverlaySpinner
        isOpen={isOverlayOpen}
        isTransparent={isSubmitting && (!isOneClickV2 || showVerifyInfo)}
        transitionDuration={{ appear: 0, exit: 225 }}
      />
      <FormikProvider value={formik}>
        <StepCard>
          <ErrorNotification text={error?.text} subText={error?.subText} />
          <Box>
            {showVerifyInfo && <VerifyInfo logger={logger} />}
            {showLandingPage && <Landing logger={logger} />}
          </Box>
          <StepCTA onClick={formik.submitForm} ctaType={ctaType} />
          <TermsOfUse
            amplitudeLogger={logger}
            buttonCaption={createAccount}
            variant="abbreviated"
          />
        </StepCard>
      </FormikProvider>
    </>
  );
};

export default PageContent;
